import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import EmptyState from '@/components/ui/EmptyState';
import SectionHeader from '@/components/ui/SectionHeader';
import Spinner from '@/components/ui/Spinner';
import { apiFetch } from '@/lib/api';

interface Segment {
  segment_id?: string;
  role?: string;
  speaker?: string;
  text?: string;
  point?: string;
  in_s?: number;
  out_s?: number;
}

interface Cut {
  title?: string;
  logline?: string;
  sections?: { scene_number?: number; intent?: string; segments?: Segment[] }[];
  coverage_gaps?: { section?: number; severity?: string; missing?: string }[];
}

/** 72.5 → "1:12" — keeps the timeline readable at a glance. */
const clock = (s?: number) => {
  const t = Math.max(0, Math.floor(s || 0));
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`;
};

/**
 * Persistent view of the approved paper cut — shows the SPOKEN WORDS per segment,
 * which the Shot List does not. Same content as the paper-cut approval gate,
 * viewable any time after approval.
 */
export default function PaperCutCard({ productionId }: { productionId: string }) {
  const { t } = useTranslation('production');
  const [cut, setCut] = useState<Cut | null>(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!productionId) return;
    apiFetch(`/api/assets/${productionId}/paper_cut.json`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('no paper cut'))))
      .then(setCut)
      .catch(() => setErr(true));
  }, [productionId]);

  if (err) {
    return (
      <EmptyState title={t('papercut.emptyTitle')}>
        {t('papercut.empty')} {t('papercut.help')}
      </EmptyState>
    );
  }
  if (!cut) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
        <Spinner /> {t('papercut.loading')}
      </div>
    );
  }

  const sections = cut.sections || [];
  const nSeg = sections.reduce((n, s) => n + (s.segments?.length || 0), 0);
  const runS = sections.reduce((n, s) => n + (s.segments || []).reduce((m, g) => m + ((g.out_s || 0) - (g.in_s || 0)), 0), 0);

  return (
    <div>
      <SectionHeader
        title={cut.title || t('step.papercut')}
        help={t('papercut.help')}
        subtitle={
          t('papercut.summary', {
            sections: t('papercut.sectionsCount', { count: sections.length }),
            segments: t('papercut.segmentsCount', { count: nSeg }),
            seconds: Math.round(runS),
          }) + (cut.logline ? ` · ${cut.logline}` : '')
        }
      />

      <div className="space-y-3">
        {sections.map((sec, i) => (
          <div key={i} className="rounded-lg border border-border bg-elevated p-3.5">
            <div className="mb-2 text-xs font-medium">
              <Badge variant="secondary" className="mr-2 text-[10px]">
                {t('papercut.section', { number: sec.scene_number ?? i + 1 })}
              </Badge>
              {sec.intent}
            </div>
            <div className="space-y-1.5">
              {(sec.segments || []).map((seg, j) => {
                const isBroll = (seg.role || '').toLowerCase() === 'broll';
                const spoken = (seg.text || '').trim();
                const isReaction = (seg.point || '').toLowerCase().includes('reaction');
                return (
                  <div key={seg.segment_id || j} className="flex items-start gap-2 text-[11px] leading-snug">
                    <span className="mt-px shrink-0 font-mono tabular-nums text-faint">
                      {clock(seg.in_s)}–{clock(seg.out_s)}
                    </span>
                    <span className="min-w-0 flex-1">
                      {isBroll ? (
                        <>
                          <Badge variant="outline" className="mr-1.5 text-[8px] uppercase">
                            {isReaction ? 'Reaction' : 'B-roll'}
                          </Badge>
                          <span className="text-muted-foreground">{seg.point || t('papercut.silentVisual')}</span>
                        </>
                      ) : spoken ? (
                        <>
                          {seg.speaker && <span className="font-semibold text-foreground">{seg.speaker}: </span>}
                          <span className="text-foreground">“{spoken}”</span>
                        </>
                      ) : (
                        <span className="italic text-muted-foreground">{seg.point || t('papercut.noTranscript')}</span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {(cut.coverage_gaps || []).length > 0 && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-[11px] text-muted-foreground">
            <div className="mb-1 font-semibold text-amber-600 dark:text-amber-400">{t('papercut.coverageGaps')}</div>
            {(cut.coverage_gaps || []).map((g, i) => (
              <div key={i}>
                [{g.severity || '?'}] section {g.section ?? '?'}: {g.missing}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
